import { useMemo } from 'react';

import type { DistrictModel } from '@snowit/types/districts';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import useAuth from '../useAuth';
import useLanguage from '../useLanguage';
import useSchemas from '../useSchemas';
import useTenants from '../useTenants';

export default () => {
  const firebase = useFirebase();
  const { authType, claims } = useAuth();
  const { activeTenants, hasTenants } = useTenants();
  const { store, setStore } = useStore();
  const { t } = useLanguage();
  const {
    // createDocument,
    // editDocument,
    // writeSubDocument,
    writeDocument,
  } = useSchemas();

  const districts = useMemo((): DistrictModel[] => {
    return store.districts.list
      .filter((district) => district.name)
      .filter((district) => {
        if (authType === 'ADMIN') return true;
        return (district.tenants || []).some((_tenant) => (claims.tenants ?? []).includes(_tenant));
      })
      .filter((district) => {
        if (!activeTenants.length) return true;
        return hasTenants(district.tenants);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [claims, authType, store.districts.list, activeTenants]);

  const getDistricts = async (): Promise<DistrictModel[]> => {
    try {
      const res = await firebase.query<DistrictModel>({
        collectionName: 'districts',
        filters: (q) => q.limit(0),
      });
      setStore('districts', { list: res });
      return res;
    } catch (error) {
      console.error('districts_get', { error });
      return [];
    } finally {
      setStore('districts', { ready: true });
    }
  };

  const getDistrict = async (districtUID: string): Promise<DistrictModel | undefined> => {
    try {
      const doc = await firebase.doc<DistrictModel>({
        collectionName: 'districts',
        id: districtUID,
      });
      if (!doc) throw new Error('no document found');
      setStore('districts', {
        list: store.districts.list.map((it) => (it.uid === doc.uid ? doc : it)),
      });
      return doc;
    } catch (error) {
      console.error({ error });
      return undefined;
    }
  };

  const writeDistrict = async (
    districtUID: string | undefined,
    data: Partial<DistrictModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.DISTRICT,
        attributes: omitter(data),
        id: isNewEntity(districtUID) ? undefined : districtUID,
      });
      return id;
    } catch (error: any) {
      console.error('writeZoneTranslations', { error });
    }
  };

  const ALL_DISTRICT: DistrictModel = {
    active: true,
    closed: true,
    icon: null,
    name: t('lzy.label.all_districts'),
    nation: null,
    permalink: 'all',
    ppuCap: null,
    ppuRegion: null,
    ppuThresholds: [],
    productsCounters: null,
    productTypes: [],
    purchasable: true,
    tenants: [],
    timeZone: null,
    uid: 'all',
    skiArea: {} as DistrictModel['skiArea'],
    turnstilesCount: 0,
    ppuClassificationByTenant: {},
  };

  return {
    getDistricts,
    getDistrict,
    writeDistrict,
    ALL_DISTRICT,
    districts,
    ready: store.districts.ready,
  };
};
